/**
 * Validates whether a CNPJ is valid (numeric or alphanumeric)
 * @param cnpj - CNPJ to validate (with or without formatting)
 * @returns true if the CNPJ is valid, false otherwise
 */
export const validateCnpj = (cnpj: string): boolean => {
  if (!cnpj || typeof cnpj !== 'string') {
    return false;
  }

  // Strip formatting while keeping only alphanumeric characters
  const cleanCnpj = cnpj.replace(/[^a-zA-Z0-9]/g, '');

  // Ensure it has 14 alphanumeric characters
  if (cleanCnpj.length !== 14) {
    return false;
  }

  // Reject sequences where every character is the same
  if (/^([a-zA-Z0-9])\1{13}$/.test(cleanCnpj)) {
    return false;
  }

  // If it is strictly numeric, validate with the check-digit algorithm
  if (/^\d+$/.test(cleanCnpj)) {
    // Reject numeric sequences consisting of the same digit
    if (/^(\d)\1{13}$/.test(cleanCnpj)) {
      return false;
    }

    // Compute the first check digit
    let sum = 0;
    let weight = 5;

    for (let i = 0; i < 12; i++) {
      sum += parseInt(cleanCnpj[i]) * weight;
      weight = weight === 2 ? 9 : weight - 1;
    }

    const firstDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11);

    // Validate the first check digit
    if (parseInt(cleanCnpj[12]) !== firstDigit) {
      return false;
    }

    // Compute the second check digit
    sum = 0;
    weight = 6;

    for (let i = 0; i < 13; i++) {
      sum += parseInt(cleanCnpj[i]) * weight;
      weight = weight === 2 ? 9 : weight - 1;
    }

    const secondDigit = sum % 11 < 2 ? 0 : 11 - (sum % 11);

    // Validate the second check digit
    return parseInt(cleanCnpj[13]) === secondDigit;
  }

  // For alphanumeric CNPJs, only validate the 14-character alphanumeric format
  // A complete validation would require Receita Federal-specific rules
  // For now, accept any 14-character alphanumeric combination
  return /^[a-zA-Z0-9]{14}$/.test(cleanCnpj);
};

/**
 * Formats a CNPJ adding dots, slash, and hyphen (numeric or alphanumeric)
 * @param cnpj - CNPJ to format
 * @returns Formatted CNPJ (00.000.000/0000-00 or AA.AAA.AAA/AAAA-AA)
 */
export const formatCnpj = (cnpj: string): string => {
  if (!cnpj || typeof cnpj !== 'string') {
    return '';
  }

  // Strip formatting while keeping only alphanumeric characters
  const cleanCnpj = cnpj.replace(/[^a-zA-Z0-9]/g, '');

  // Limit to 14 characters
  const limitedCnpj = cleanCnpj.slice(0, 14);

  // Apply formatting: XX.XXX.XXX/XXXX-XX
  return limitedCnpj
    .replace(/^([a-zA-Z0-9]{2})([a-zA-Z0-9])/, '$1.$2')
    .replace(/^([a-zA-Z0-9]{2})\.([a-zA-Z0-9]{3})([a-zA-Z0-9])/, '$1.$2.$3')
    .replace(/\.([a-zA-Z0-9]{3})([a-zA-Z0-9])/, '.$1/$2')
    .replace(/([a-zA-Z0-9]{4})([a-zA-Z0-9])/, '$1-$2');
};

/**
 * Removes CNPJ formatting
 * @param cnpj - Formatted CNPJ
 * @returns CNPJ containing only letters and numbers (no formatting)
 */
export const unformatCnpj = (cnpj: string): string => {
  return cnpj.replace(/[^a-zA-Z0-9]/g, '');
};
